import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Plus, Pencil, Trash2, Search, Mail, Shield, FolderOpen, X, BookOpen, Clock, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

// Searchable multi-select for project titles
const TitleMultiSelect = ({ selected, onChange }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/distinct-titles?q=${encodeURIComponent(query)}`);
        setOptions(res.data);
      } catch (e) { /* ignore */ }
    };
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (title) => {
    if (selected.includes(title)) {
      onChange(selected.filter(t => t !== title));
    } else {
      onChange([...selected, title]);
    }
  };

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-[40px] cursor-text" onClick={() => setOpen(true)}>
        {selected.map(t => (
          <Badge key={t} variant="secondary" className="text-xs gap-1">
            {t}
            <button onClick={(e) => { e.stopPropagation(); toggle(t); }}><X className="w-3 h-3" /></button>
          </Badge>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? "Search project titles..." : ""}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm"
          data-testid="title-search-input"
        />
      </div>
      {open && options.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-popover border rounded-md shadow-lg">
          {options.filter(o => !selected.includes(o)).slice(0, 20).map(title => (
            <button
              key={title}
              onClick={() => { toggle(title); setQuery(''); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted truncate"
            >
              {title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const GtsProEmails = () => {
  const { user } = useOutletContext();
  const isOwner = user?.role === 'owner';
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formEmail, setFormEmail] = useState('');
  const [formStatus, setFormStatus] = useState('active');
  const [formTitles, setFormTitles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('emails');

  // Log book state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedEmailLog, setSelectedEmailLog] = useState(null);
  const [emailLogDetail, setEmailLogDetail] = useState(null);

  useEffect(() => { loadEmails(); }, []);

  const loadEmails = async () => {
    try {
      const res = await api.get('/gts-pro-emails');
      setEmails(res.data);
    } catch (error) {
      toast.error('Failed to load GTS Pro emails');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await api.get('/gts-email-logs');
      setLogs(res.data);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadEmailLogDetail = async (email) => {
    setSelectedEmailLog(email);
    try {
      const res = await api.get(`/gts-email-logs/${encodeURIComponent(email)}`);
      setEmailLogDetail(res.data);
    } catch (error) {
      toast.error('Failed to load email log');
    }
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return emails;
    const q = searchQuery.toLowerCase();
    return emails.filter(e =>
      e.email.toLowerCase().includes(q) ||
      (e.project_titles || []).some(t => t.toLowerCase().includes(q))
    );
  }, [emails, searchQuery]);

  const handleSave = async () => {
    try {
      const payload = { email: formEmail, status: formStatus, project_titles: formTitles };
      if (editItem) {
        await api.put(`/owner/gts-pro-emails/${editItem.email_id}`, payload);
        toast.success('Email updated');
      } else {
        await api.post('/owner/gts-pro-emails', payload);
        toast.success('Email added');
      }
      setShowAdd(false);
      setEditItem(null);
      resetForm();
      loadEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const resetForm = () => { setFormEmail(''); setFormStatus('active'); setFormTitles([]); };

  const handleDelete = async (emailId) => {
    if (!confirm('Delete this email entry?')) return;
    try {
      await api.delete(`/owner/gts-pro-emails/${emailId}`);
      toast.success('Deleted');
      loadEmails();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormEmail(item.email);
    setFormStatus(item.status);
    setFormTitles(item.project_titles || []);
    setShowAdd(true);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/owner/gts-pro-emails/import', fd);
      toast.success(res.data.message);
      loadEmails();
      setShowImport(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const activeCount = emails.filter(e => e.status === 'active').length;

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="gts-pro-title">GTS Pro Emails</h1>
            <p className="text-muted-foreground">{emails.length} total — {activeCount} active</p>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-gts-btn">
                <Upload className="w-4 h-4 mr-2" /> Import
              </Button>
              <Button size="sm" onClick={() => { setEditItem(null); resetForm(); setShowAdd(true); }} data-testid="add-gts-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Email
              </Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'logs') loadLogs(); }}>
          <TabsList>
            <TabsTrigger value="emails" className="flex items-center gap-1"><Mail className="w-4 h-4" /> Emails</TabsTrigger>
            {isOwner && <TabsTrigger value="logs" className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> Log Book</TabsTrigger>}
          </TabsList>

          {/* Emails Tab */}
          <TabsContent value="emails" className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search emails or project titles..." className="pl-10" />
            </div>

            <Card>
              <CardContent className="p-0">
                {filtered.length === 0 ? (
                  <div className="py-16 text-center">
                    <Mail className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground">{emails.length === 0 ? 'No GTS Pro emails yet' : 'No matches found'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">#</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Project Titles</TableHead>
                          {isOwner && <TableHead className="w-24 text-center">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((item, idx) => (
                          <TableRow key={item.email_id}>
                            <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                            <TableCell className="font-medium text-sm">{item.email}</TableCell>
                            <TableCell>
                              {item.status === 'active'
                                ? <Badge className="bg-green-100 text-green-700 text-xs"><Shield className="w-3 h-3 mr-1" />Active</Badge>
                                : <Badge className="bg-gray-100 text-gray-500 text-xs">Inactive</Badge>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap max-w-[400px]">
                                {(item.project_titles || []).map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px]">{t}</Badge>
                                ))}
                                {(!item.project_titles || item.project_titles.length === 0) && (
                                  <span className="text-xs text-muted-foreground">No projects mapped</span>
                                )}
                              </div>
                            </TableCell>
                            {isOwner && (
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="h-7 w-7 p-0">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.email_id)} className="h-7 w-7 p-0 text-destructive">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Log Book Tab (Owner Only) */}
          {isOwner && (
            <TabsContent value="logs" className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Email Usage Log
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {logsLoading ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">Loading logs...</p>
                  ) : logs.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">No usage logs yet. Logs are created when MapPro mappings change.</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Summary per email */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[...new Set(logs.map(l => l.gts_email))].map(email => {
                          const emailLogs = logs.filter(l => l.gts_email === email);
                          const uniqueUsers = [...new Set(emailLogs.map(l => l.translator_name))];
                          const latestAssign = emailLogs.find(l => l.action === 'assigned');
                          return (
                            <Card key={email} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => loadEmailLogDetail(email)}>
                              <CardContent className="pt-4 pb-3">
                                <p className="font-mono text-sm font-medium truncate">{email}</p>
                                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{uniqueUsers.length} users</span>
                                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{emailLogs.length} events</span>
                                </div>
                                {latestAssign && (
                                  <p className="text-xs mt-1 text-green-600">Current: {latestAssign.translator_name}</p>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>

                      {/* Full log table */}
                      <div className="overflow-x-auto max-h-96 border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="whitespace-nowrap">Timestamp</TableHead>
                              <TableHead className="whitespace-nowrap">GTS Email</TableHead>
                              <TableHead className="whitespace-nowrap">Translator</TableHead>
                              <TableHead className="whitespace-nowrap">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {logs.map(log => (
                              <TableRow key={log.log_id}>
                                <TableCell className="text-xs whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</TableCell>
                                <TableCell className="text-xs font-mono">{log.gts_email}</TableCell>
                                <TableCell className="text-xs">{log.translator_name} <span className="text-muted-foreground">({log.translator_email})</span></TableCell>
                                <TableCell>
                                  {log.action === 'assigned'
                                    ? <Badge className="bg-green-100 text-green-700 text-[10px]">Assigned</Badge>
                                    : <Badge className="bg-orange-100 text-orange-700 text-[10px]">Released</Badge>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditItem(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit GTS Pro Email' : 'Add GTS Pro Email'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Email Address</Label>
              <Input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="user@client.com" disabled={!!editItem} />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={formStatus} onValueChange={setFormStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Project Titles</Label>
              <TitleMultiSelect selected={formTitles} onChange={setFormTitles} />
              <p className="text-xs text-muted-foreground">Search and select from Client Projects / Billings main titles</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editItem ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import GTS Pro Emails</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload Excel/CSV with columns: <strong>Email</strong> and <strong>Status</strong>. Project titles must be assigned manually after import.</p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
              <input type="file" id="gts-import" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleImport} disabled={uploading} />
              <Button variant="outline" onClick={() => document.getElementById('gts-import').click()} disabled={uploading}>
                <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Importing...' : 'Choose File'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Log Detail Dialog */}
      <Dialog open={!!selectedEmailLog} onOpenChange={(o) => { if (!o) setSelectedEmailLog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5" /> Log: {selectedEmailLog}</DialogTitle>
          </DialogHeader>
          {emailLogDetail ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{emailLogDetail.total_users}</p>
                  <p className="text-xs text-muted-foreground">Total Users</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg col-span-2">
                  <p className="text-sm font-semibold text-green-700">{emailLogDetail.current_user || 'Unassigned'}</p>
                  <p className="text-xs text-muted-foreground">Current User</p>
                </div>
              </div>
              {emailLogDetail.previous_users.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">All users who used this email:</p>
                  <div className="flex gap-1 flex-wrap">
                    {emailLogDetail.previous_users.map(u => <Badge key={u} variant="outline" className="text-xs">{u}</Badge>)}
                  </div>
                </div>
              )}
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Time</TableHead>
                      <TableHead className="text-xs">Translator</TableHead>
                      <TableHead className="text-xs">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogDetail.logs.map(l => (
                      <TableRow key={l.log_id}>
                        <TableCell className="text-[10px] whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{l.translator_name}</TableCell>
                        <TableCell>
                          {l.action === 'assigned'
                            ? <Badge className="bg-green-100 text-green-700 text-[10px]">Assigned</Badge>
                            : <Badge className="bg-orange-100 text-orange-700 text-[10px]">Released</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default GtsProEmails;
